/* eslint-disable import/no-cycle */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import styled from 'styled-components';
import {
  DataTable,
  Loader,
  styles,
  SdirButton,
  TextLink,
  CheckIndicator,
  SdiArrow,
  SdirModal,
  Tooltip,
} from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  AlternativeDetails,
  SeagoingQueryResult,
  ServiceReportExtendedResult,
  ServiceReportSimulatorAlternatives,
} from '@sdir/httpclient/lib/clients/aps/application';
import {
  ErrorWithTrace,
  FetchedAttachmentDetails,
  RegisterSeagoingModal,
} from '@sdir/blueprint.aps/lib/components';
import NewApplicationLayout from '../../NewApplicationLayout';
import { qualificationsApi, seagoingApi, seagoingRegistrationApi } from '../../../../httpclient';
import { useCurrentApplication, useGetCertificateName } from '../../../../services/hooks';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { QualificationType } from '../../../../types';
import { DisplayDateFormat } from '../../../../helpers';
import {
  NewSeagoingTableColumns,
  NewSeagoingTableColumnsMobile,
} from '../../../../helpers/TableHeaders';
import {
  formatBackendValidationErrors,
  submitNewSeagoingCreate,
  submitNewSeagoingUpdate,
} from '../../../../helpers/seagoingApiHelpers';
import { routes } from '../../../../constants/routes';
import { getAttachmentUrl } from '../../../../helpers/attachmentApiHelpers';
import { FileViewer } from '../../../Molecules/FileDrop/FileViewer';
import { DEFAULT_LANG } from '../../../../helpers/languageHelpers';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

type DocumentPreview = {
  name?: string;
  file?: File;
  url?: string;
  uid?: string;
};

export const AddSeagoingNew: FC = () => {
  const intl = useIntl();
  const { requirement = '', applicationUid = '' } = useParams<{
    requirement: string;
    applicationUid: string;
  }>();
  const location = useLocation();
  const navigate = useNavigateWithLang();
  const search = location?.search;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const fromMissingDocumentation = searchParams.get('from') === 'missingdocumentation';
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [selectedSeagoing, setSelectedSeagoing] = useState<SeagoingQueryResult>();
  const [selectedAttachment, setSelectedAttachment] = useState<FetchedAttachmentDetails>();
  const [loadingSeagoing, setLoadingSeagoing] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [submitValidationErrors, setSubmitValidationErrors] = useState<any>(undefined);
  const [submitErrors, setSubmitErrors] = useState<any>(undefined);
  const [getErrors, setGetErrors] = useState<any>(undefined);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [toggleApprovedUnused, setToggleApprovedUnused] = useState<boolean>(false);
  const [toggleApproved, setToggleApproved] = useState<boolean>(false);
  const [filePreview, setFilePreview] = useState<DocumentPreview>();

  const {
    application,
    qualifications,
    refresh: refreshApplication,
  } = useCurrentApplication({
    includeQualifications: true,
  });

  const certificateName = useGetCertificateName(application.result?.certificate || '');

  const { result: formdataResult, loading: formdataLoading } = useApi({
    apiFunction: () => seagoingRegistrationApi.v2SeagoingregistrationGetvalidformdatalanguageGet(),
    callApiOnInit: true,
  });

  const { hasRequirements, ingress } = useMemo(() => {
    const result = qualifications?.result;
    if (!result) return { hasRequirements: false, ingress: '' };
    const [type, alternativeNr] = requirement?.split('-');
    const index = parseInt(alternativeNr, 10) ? parseInt(alternativeNr, 10) - 1 : -1;
    let items = result[type]?.items || result[type]?.[index]?.items || [];
    items = items.filter((item) => item.type === QualificationType.SeagoingTime);
    return {
      hasRequirements: !!(items?.length > 0),
      ingress: intl.formatMessage({ id: 'addseagoing.apply.for' }, { cert: certificateName }),
    };
  }, [requirement, qualifications?.result, certificateName]);

  const occupations = useMemo(() => {
    return formdataResult?.occupationFormData || [];
  }, [formdataResult?.occupationFormData]);

  const {
    result: simulatedData = [],
    errorObject: simulatedError,
    loading: simulatedLoading,
    callApi: getSimulatedResult,
  } = useApi({
    apiFunction: (uid) =>
      qualificationsApi.v1QualificationsGetservicereportqualificationsApplicationUidGet(uid),
    callApiOnInit: false,
  });

  const filePreviewType = useMemo(() => {
    const { file, name } = filePreview || {};
    if (file?.type) return file.type;
    if (name?.indexOf('.pdf') !== -1) return 'application/pdf';
    if (name?.indexOf('.png') !== -1) return 'image/png';
    if (name?.indexOf('.jpg') !== -1 || name?.indexOf('.jpeg') !== -1) return 'image/jpeg';
    return 'application/pdf';
  }, [filePreview]);

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    getSimulatedResult(applicationUid);
  }, [applicationUid]);

  useEffect(() => {
    if (applicationUid && selectedSeagoing?.attachmentUid) {
      getAttachmentUrl(applicationUid, {
        blobName: selectedSeagoing?.blobName,
        uid: selectedSeagoing?.attachmentUid,
      }).then((result) => {
        setFilePreview({
          name: selectedSeagoing?.blobName || '',
          url: result,
          file: undefined,
          uid: selectedSeagoing.attachmentUid,
        });
      });
    }
  }, [applicationUid, selectedSeagoing]);

  const fetchedSeagoing: {
    currentUsed: ServiceReportExtendedResult[];
    currentUnused: ServiceReportExtendedResult[];
    details: AlternativeDetails[];
    previous: ServiceReportExtendedResult[];
    previousUnused: ServiceReportExtendedResult[];
  } = useMemo(() => {
    const [type, alternativeNr] = requirement?.split('-');
    const fetched: ServiceReportSimulatorAlternatives[] = simulatedData?.[type] || [];
    if (alternativeNr) {
      const alternative = fetched?.find((item) => `${item?.alternativeNumber}` === alternativeNr);
      return {
        currentUsed: alternative?.applicationServiceReportsUsed || [],
        currentUnused: alternative?.unusedApplicationServiceReports || [],
        details: alternative?.alternativeDetails || [],
        previous: alternative?.approvedServiceReportsUsed || [],
        previousUnused: alternative?.unusedApprovedServiceReports || [],
      };
    }
    return {
      currentUsed: [],
      currentUnused: [],
      details: [],
      previous: [],
      previousUnused: [],
    };
  }, [requirement, simulatedData]);

  const onNew = () => {
    setSelectedSeagoing(undefined);
    setSelectedAttachment(undefined);
    setFilePreview(undefined);
    setShowRegistration(true);
  };

  const onEdit = (selected?: ServiceReportExtendedResult) => {
    if (selected?.uid) {
      setShowRegistration(true);
      setLoadingSeagoing(true);
      seagoingApi
        .v1SeagoingGetservicereportforapplicationSeagoingUidGet(applicationUid, selected.uid)
        .then((res) => res.data)
        .then((result: SeagoingQueryResult) => {
          setSelectedSeagoing(result);
          setSelectedAttachment({ data: null, name: result?.blobName || '' });
          setLoadingSeagoing(false);
          setGetErrors(undefined);
        })
        .catch((error: AxiosError) => {
          setLoadingSeagoing(false);
          setGetErrors(error);
          console.log('error: ', error);
        });
    }
  };

  const { tableDataCurrent, tableDataNotRelevant, tableDataCurrentTotal } = useMemo(() => {
    let total = 0;
    const used = fetchedSeagoing?.currentUsed?.map((rowData) => {
      const { activity, vessel } = rowData || {};
      const occupation = occupations?.find((o) => o.value === activity?.occupationCode);
      const startDate = DisplayDateFormat(activity?.startDate);
      const endDate = DisplayDateFormat(activity?.endDate);
      total += activity?.totalDays || 0;
      return {
        status: (
          <span title={intl.formatMessage({ id: 'seagoing.ok.tooltip' })}>
            <CheckIndicator size="s" status="ok" bgColor={styles.colors.alert.successDark} />
          </span>
        ),
        period: `${startDate} - ${endDate}`,
        vessel: vessel?.name || '',
        occupation: occupation?.name || '',
        seagoingTime: `${activity?.totalDays} ${intl.formatMessage({ id: 'seagoing.days' })}`,
        actions: (
          <TextLink
            text={intl.formatMessage({ id: 'general.button.edit' })}
            type="button"
            click={() => onEdit(rowData)}
          />
        ),
        childContent: isMobile ? (
          <RowMobileContent
            rowData={rowData}
            onEdit={onEdit}
            occupation={occupation?.name || ''}
            fm={intl.formatMessage}
          />
        ) : null,
      };
    });
    const unused = fetchedSeagoing?.currentUnused?.map((rowData) => {
      const { activity, vessel } = rowData || {};
      const occupation = occupations?.find((o) => o.value === activity?.occupationCode);
      const startDate = DisplayDateFormat(activity?.startDate);
      const endDate = DisplayDateFormat(activity?.endDate);
      return {
        status: (
          <span title={intl.formatMessage({ id: 'seagoing.warning.tooltip' })}>
            <CheckIndicator size="s" status="warning" bgColor={styles.colors.alert.warningDark} />
          </span>
        ),
        period: `${startDate} - ${endDate}`,
        vessel: vessel?.name || '',
        occupation: occupation?.name || '',
        seagoingTime: `${activity?.totalDays} ${intl.formatMessage({ id: 'seagoing.days' })}`,
        actions: (
          <TextLink
            text={intl.formatMessage({ id: 'general.button.edit' })}
            type="button"
            click={() => onEdit(rowData)}
          />
        ),
        childContent: isMobile ? (
          <RowMobileContent
            rowData={rowData}
            onEdit={onEdit}
            occupation={occupation?.name || ''}
            fm={intl.formatMessage}
          />
        ) : null,
      };
    });
    return {
      tableDataCurrent: used || [],
      tableDataNotRelevant: unused || [],
      tableDataCurrentTotal: Math.round(total * 100) / 100,
    };
  }, [intl, fetchedSeagoing, occupations]);

  const { tableDataPrevious, tableDataPreviousTotal } = useMemo(() => {
    let total = 0;
    const data = fetchedSeagoing?.previous?.map((rowData) => {
      const { activity, vessel } = rowData || {};
      const occupation = occupations?.find((o) => o.value === activity?.occupationCode);
      const startDate = DisplayDateFormat(activity?.startDate);
      const endDate = DisplayDateFormat(activity?.endDate);
      total += activity?.totalDays || 0;
      return {
        status: (
          <span title={intl.formatMessage({ id: 'seagoing.ok.tooltip' })}>
            <CheckIndicator size="s" status="ok" bgColor={styles.colors.alert.successDark} />
          </span>
        ),
        period: `${startDate} - ${endDate}`,
        vessel: vessel?.name || '',
        occupation: occupation?.name || '',
        seagoingTime: `${activity?.totalDays} ${intl.formatMessage({ id: 'seagoing.days' })}`,
        childContent: isMobile ? (
          <RowMobileContent
            rowData={rowData}
            occupation={occupation?.name || ''}
            fm={intl.formatMessage}
          />
        ) : null,
      };
    });
    return {
      tableDataPrevious: data,
      tableDataPreviousTotal: Math.round(total * 100) / 100,
    };
  }, [intl, fetchedSeagoing, occupations]);

  const { tableDataPreviousUnused, tableDataPreviousUnusedTotal } = useMemo(() => {
    let total = 0;
    const data = fetchedSeagoing?.previousUnused?.map((rowData) => {
      const { activity, vessel } = rowData || {};
      const occupation = occupations?.find((o) => o.value === activity?.occupationCode);
      const startDate = DisplayDateFormat(activity?.startDate);
      const endDate = DisplayDateFormat(activity?.endDate);
      total += activity?.totalDays || 0;
      return {
        status: (
          <span title={intl.formatMessage({ id: 'seagoing.warning.tooltip' })}>
            <CheckIndicator size="s" status="warning" bgColor={styles.colors.alert.warningDark} />
          </span>
        ),
        period: `${startDate} - ${endDate}`,
        vessel: vessel?.name || '',
        occupation: occupation?.name || '',
        seagoingTime: `${activity?.totalDays} ${intl.formatMessage({ id: 'seagoing.days' })}`,
        childContent: isMobile ? (
          <RowMobileContent
            rowData={rowData}
            occupation={occupation?.name || ''}
            fm={intl.formatMessage}
          />
        ) : null,
      };
    });
    return {
      tableDataPreviousUnused: data,
      tableDataPreviousUnusedTotal: Math.round(total * 100) / 100,
    };
  }, [intl, fetchedSeagoing, occupations]);

  const onDelete = async () => {
    try {
      setLoadingDelete(true);
      await seagoingApi.v1SeagoingDeleteservicereportforapplicationCommandDelete({
        serviceReportUId: selectedSeagoing?.uid || '',
        applicationUid: applicationUid || '',
      });
      setLoadingDelete(false);
      onCloseRegistration();
      getSimulatedResult(applicationUid, intl.locale || DEFAULT_LANG);
    } catch (error) {
      setSubmitErrors(error);
      setLoadingDelete(false);
      setShowRegistration(true);
    }
  };

  const onSelectAttachment = (name?: string, file?: File, uid?: string) => {
    setSelectedAttachment(name ? { data: null, name: name || '' } : undefined);
    if (!name && !file && !uid) {
      setFilePreview(undefined);
    } else if (uid && applicationUid) {
      getAttachmentUrl(applicationUid, {
        blobName: name,
        uid,
      }).then((result) => {
        setFilePreview({ name, url: result, file: undefined, uid });
      });
    } else {
      setFilePreview({ name: name || '', url: '', file, uid: uid || '' });
    }
  };

  const onCloseRegistration = (refresh: boolean = true) => {
    setShowRegistration(false);
    setSelectedAttachment(undefined);
    setSelectedSeagoing(undefined);
    setSubmitValidationErrors(undefined);
    setSubmitErrors(undefined);
    setGetErrors(undefined);
    setLoadingSubmit(false);
    if (refresh) {
      refreshApplication?.();
    }
  };

  const onSubmit = async (values: any) => {
    if (!application?.result) return;
    setSubmitValidationErrors(undefined);
    setSubmitErrors(undefined);
    setLoadingSubmit(true);
    if (selectedSeagoing?.uid) {
      await submitNewSeagoingUpdate(values, application.result, selectedSeagoing)
        .then(() => {
          onCloseRegistration();
          getSimulatedResult(applicationUid, intl.locale || DEFAULT_LANG);
        })
        .catch((error: AxiosError) => {
          if (error?.response?.status === 422) {
            // Handle backend validation errors
            const errors = error?.response?.data?.fieldErrors || {};
            const { fieldErrors, otherError } = formatBackendValidationErrors(errors);
            setSubmitValidationErrors(fieldErrors);
            if (otherError) {
              setSubmitErrors(otherError);
            } else if (Object.keys(fieldErrors)?.length === 0) {
              setSubmitErrors(error?.response?.data?.message || null);
            }
          } else if (error?.response?.status === 413) {
            setSubmitErrors(intl.formatMessage({ id: 'addseagoing.save.error.toobig' }));
          } else {
            setSubmitErrors(error);
          }
          setLoadingSubmit(false);
        });
    } else {
      await submitNewSeagoingCreate(values, application.result)
        .then(() => {
          onCloseRegistration();
          getSimulatedResult(applicationUid, intl.locale || DEFAULT_LANG);
        })
        .catch((error: AxiosError) => {
          if (error?.response?.status === 422) {
            // Handle backend validation errors
            const errors = error?.response?.data?.fieldErrors || {};
            const { fieldErrors, otherError } = formatBackendValidationErrors(errors);
            setSubmitValidationErrors(fieldErrors);
            if (otherError) {
              setSubmitErrors(otherError);
            } else if (Object.keys(fieldErrors)?.length === 0) {
              setSubmitErrors(error?.response?.data?.message || null);
            }
          } else if (error?.response?.status === 413) {
            setSubmitErrors(intl.formatMessage({ id: 'addseagoing.save.error.toobig' }));
          } else {
            setSubmitErrors(error);
          }
          setLoadingSubmit(false);
        });
    }
  };

  const totalMissingDays = useMemo(() => {
    let total = 0;
    fetchedSeagoing?.details?.forEach((detail) => {
      total += detail?.qualifiedDaysMissing || 0;
    });
    return Math.round(total * 100) / 100;
  }, [fetchedSeagoing?.details]);

  const onBackToRequirements = useCallback(() => {
    const route = fromMissingDocumentation
      ? routes.application.uploadMissingDocumentation
      : routes.application.qualification;
    const url = generatePath(route, {
      applicationUid: applicationUid || '',
    });
    navigate(url);
  }, [applicationUid, intl, fromMissingDocumentation, navigate]);

  const onExitClick = () => {
    navigate(routes.certificateOverview);
  };

  const onNextClick = () => {
    if (totalMissingDays) {
      setShowConfirmModal(true);
    } else {
      onBackToRequirements();
    }
  };

  const isLoading = qualifications?.loading || application?.loading || simulatedLoading;
  const showSeagoings = !isLoading && hasRequirements && !simulatedError;

  return (
    <EditApplicationGuard>
      <NewApplicationLayout
        exitLabel={intl.formatMessage({ id: 'general.form.back' })}
        mainContentMaxWidth="100%"
        canNavBack
        canNavNext={!simulatedError}
        hideTimeline
        heading={intl.formatMessage({ id: 'addseagoing.title' })}
        hideTopHeading
        navBackLabel={intl.formatMessage({ id: 'general.form.goback' })}
        navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
        onNavNextClick={onNextClick}
        onNavBackClick={onBackToRequirements}
        onExitClick={onExitClick}
      >
        {isLoading && <Loader />}
        {simulatedError && (
          <ErrorWithTrace
            error={intl.formatMessage({ id: 'addseagoing.fetch.error' })}
            trace={simulatedError?.response?.headers?.traceresponse}
          />
        )}
        {showSeagoings && (
          <>
            <Ingress>{ingress}</Ingress>
            <MissingDays>
              {fetchedSeagoing?.details?.map((detail, index) => {
                if (!detail.qualifiedDaysMissing && detail.qualifiedDaysMissing !== 0) {
                  return undefined;
                }
                return (
                  <div key={detail?.requirement}>
                    <FormattedMessage
                      id="addseagoing.missingdays"
                      values={{
                        days: detail?.qualifiedDaysMissing,
                        requirement: detail?.requirement,
                        b: (chunks) => <b>{chunks}</b>,
                        i: (chunks) => <i>{chunks}</i>,
                      }}
                    />
                    {fetchedSeagoing.details.length > index + 1 && (
                      <b>, {intl.formatMessage({ id: 'general.separator.and' })}</b>
                    )}
                  </div>
                );
              })}
            </MissingDays>
            <NewSeagoingButton>
              <SdirButton
                onClick={onNew}
                text={intl.formatMessage({ id: 'addseagoing.button.new' })}
                type="primary"
              />
            </NewSeagoingButton>
            {tableDataCurrent?.length > 0 && (
              <CurrentSeagoing>
                <h2>{intl.formatMessage({ id: 'addseagoing.relevant.heading' })}</h2>
                <DataTable
                  columns={
                    isMobile
                      ? NewSeagoingTableColumnsMobile(intl.formatMessage)
                      : NewSeagoingTableColumns(intl.formatMessage)
                  }
                  collapsable={isMobile}
                  collapsableText={[
                    intl.formatMessage({ id: 'general.table.viewdetails' }),
                    intl.formatMessage({ id: 'general.table.hidedetails' }),
                  ]}
                  data={tableDataCurrent}
                >
                  <TableTotal>
                    <span>
                      {`${intl.formatMessage({
                        id: 'general.table.total',
                      })} ${tableDataCurrentTotal} ${intl.formatMessage({ id: 'seagoing.days' })}`}
                    </span>
                  </TableTotal>
                </DataTable>
              </CurrentSeagoing>
            )}
            {tableDataNotRelevant?.length > 0 && (
              <NotRelevantSeagoing>
                <h2>{intl.formatMessage({ id: 'addseagoing.notrelevant.heading' })}</h2>
                <DataTable
                  columns={
                    isMobile
                      ? NewSeagoingTableColumnsMobile(intl.formatMessage)
                      : NewSeagoingTableColumns(intl.formatMessage)
                  }
                  collapsable={isMobile}
                  collapsableText={[
                    intl.formatMessage({ id: 'general.table.viewdetails' }),
                    intl.formatMessage({ id: 'general.table.hidedetails' }),
                  ]}
                  data={tableDataNotRelevant}
                />
              </NotRelevantSeagoing>
            )}
            <MissingBox className={!totalMissingDays ? 'valid' : ''}>
              {!totalMissingDays ? (
                <>
                  <CheckIndicator status="valid" />
                  <p>{intl.formatMessage({ id: 'addseagoing.alldocumented' })}</p>
                </>
              ) : (
                <>
                  <CheckIndicator status="warning" />
                  <MissingDocumentationRows>
                    {fetchedSeagoing?.details?.map((detail, index) => {
                      if (!detail.qualifiedDaysMissing && detail.qualifiedDaysMissing !== 0) {
                        return undefined;
                      }
                      return (
                        <div key={detail?.requirement}>
                          <FormattedMessage
                            id="addseagoing.missingdays2"
                            values={{
                              days: detail?.qualifiedDaysMissing,
                              requirement: detail?.requirement,
                              b: (chunks) => <b>{chunks}</b>,
                            }}
                          />
                        </div>
                      );
                    })}
                  </MissingDocumentationRows>
                  <StyledTooltip width="40rem" placement="bottom-left" small>
                    {intl.formatMessage({ id: 'addseagoing.incomplete.tooltip' })}
                  </StyledTooltip>
                </>
              )}
            </MissingBox>
            {tableDataPrevious?.length > 0 && (
              <PreviouslyApprovedSeagoing>
                <HeadingPrevious
                  onClick={() => setToggleApproved(!toggleApproved)}
                  onKeyUp={(e) => e.key === 'Enter' && setToggleApproved(!toggleApproved)}
                  tabIndex={0}
                  role="button"
                >
                  <h2>{intl.formatMessage({ id: 'addseagoing.approved.heading' })}</h2>
                  <SdiArrow size="s" height="12px" direction={toggleApproved ? 'up' : 'down'} />
                </HeadingPrevious>
                {toggleApproved && (
                  <DataTable
                    columns={
                      isMobile
                        ? NewSeagoingTableColumnsMobile(intl.formatMessage)
                        : NewSeagoingTableColumns(intl.formatMessage)
                    }
                    collapsable={isMobile}
                    collapsableText={[
                      intl.formatMessage({ id: 'general.table.viewdetails' }),
                      intl.formatMessage({ id: 'general.table.hidedetails' }),
                    ]}
                    data={tableDataPrevious}
                  >
                    <TableTotal>
                      <span>
                        {`${intl.formatMessage({
                          id: 'general.table.total',
                        })} ${tableDataPreviousTotal} ${intl.formatMessage({
                          id: 'seagoing.days',
                        })}`}
                      </span>
                    </TableTotal>
                  </DataTable>
                )}
              </PreviouslyApprovedSeagoing>
            )}
            {tableDataPreviousUnused?.length > 0 && (
              <PreviouslyApprovedSeagoing>
                <HeadingPrevious
                  onClick={() => setToggleApprovedUnused(!toggleApprovedUnused)}
                  onKeyUp={(e) =>
                    e.key === 'Enter' && setToggleApprovedUnused(!toggleApprovedUnused)
                  }
                  tabIndex={0}
                  role="button"
                >
                  <h2>{intl.formatMessage({ id: 'addseagoing.approvedunused.heading' })}</h2>
                  <SdiArrow
                    size="s"
                    height="12px"
                    direction={toggleApprovedUnused ? 'up' : 'down'}
                  />
                </HeadingPrevious>
                {toggleApprovedUnused && (
                  <DataTable
                    columns={
                      isMobile
                        ? NewSeagoingTableColumnsMobile(intl.formatMessage)
                        : NewSeagoingTableColumns(intl.formatMessage)
                    }
                    collapsable={isMobile}
                    collapsableText={[
                      intl.formatMessage({ id: 'general.table.viewdetails' }),
                      intl.formatMessage({ id: 'general.table.hidedetails' }),
                    ]}
                    data={tableDataPreviousUnused}
                  >
                    <TableTotal>
                      <span>
                        {`${intl.formatMessage({
                          id: 'general.table.total',
                        })} ${tableDataPreviousUnusedTotal} ${intl.formatMessage({
                          id: 'seagoing.days',
                        })}`}
                      </span>
                    </TableTotal>
                  </DataTable>
                )}
              </PreviouslyApprovedSeagoing>
            )}
          </>
        )}
        <RegisterSeagoingModal
          application={application.result}
          formData={formdataResult}
          isOpen={showRegistration}
          loading={loadingSeagoing || formdataLoading}
          submitting={loadingDelete || loadingSubmit}
          onClose={() => onCloseRegistration(false)}
          onSubmit={onSubmit}
          onDelete={onDelete}
          submitErrors={submitErrors}
          getErrors={getErrors}
          submitValidationErrors={submitValidationErrors}
          onSelectAttachment={onSelectAttachment}
          hideDelete={!selectedSeagoing?.uid}
          seagoing={selectedSeagoing}
          attachment={selectedAttachment}
          extraContentRight={
            filePreview && (
              <FilePreview>
                {filePreview.file && <FileViewer file={filePreview.file as any} />}
                {!filePreview.file && filePreview.url && (
                  <FileViewer file={{} as any} url={filePreview.url} type={filePreviewType} />
                )}
              </FilePreview>
            )
          }
        />
        <SdirModal
          closeButton
          onCancel={() => setShowConfirmModal(false)}
          onOK={onBackToRequirements}
          okText="OK"
          cancelText={intl.formatMessage({ id: 'general.form.cancel' })}
          visible={showConfirmModal}
          width="375px"
        >
          <h2>{intl.formatMessage({ id: 'addseagoing.confirm.heading' })}</h2>
          <p>
            <FormattedMessage
              id="addseagoing.confirm.description"
              values={{
                days: totalMissingDays,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </p>
          <StyledTooltip width="40rem" placement="top-right" small>
            {intl.formatMessage({ id: 'addseagoing.incomplete.tooltip2' })}
          </StyledTooltip>
        </SdirModal>
      </NewApplicationLayout>
    </EditApplicationGuard>
  );
};

interface RowMobileContentProps {
  rowData: ServiceReportExtendedResult;
  occupation?: string;
  fm: any;
  onEdit?: (rowData: ServiceReportExtendedResult) => void;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, occupation, fm, onEdit }) => {
  const startDate = DisplayDateFormat(rowData.activity?.startDate);
  const endDate = DisplayDateFormat(rowData.activity?.endDate);
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'seagoing.table.period' })}</SubHeader>
        <SubValue>{`${startDate} - ${endDate}`}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'seagoing.table.occupation' })}</SubHeader>
        <SubValue>{occupation}</SubValue>
      </MobileContentItem>
      {onEdit && (
        <MobileContentItem>
          <TextLink
            text={fm({ id: 'general.button.edit' })}
            type="button"
            click={() => onEdit(rowData)}
          />
        </MobileContentItem>
      )}
    </>
  );
};

export const MissingBox = styled.div`
  padding: 2rem;
  margin-top: 15px;
  display: flex;
  flex-direction: row;
  box-shadow: ${({ theme }) => theme.card.boxShadow};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border: 1px solid ${({ theme }) => theme.colors.alert.warningDark};
  background-color: ${({ theme }) => theme.colors.alert.warningLight};
  transition: 250ms ease;

  &.valid {
    border: 1px solid ${({ theme }) => theme.colors.alert.successDark};
    background-color: ${({ theme }) => theme.colors.alert.successLight};
  }

  & > *:first-child {
    margin-right: 30px;
    min-width: 32px;
  }
  &:not(.valid) > *:last-child {
    margin-left: 15px;
    align-self: flex-end;
  }

  p {
    margin: 8px 0 0 0;
  }
`;

const MissingDocumentationRows = styled.div`
  & > div {
    margin-top: 8px;
  }
`;

const Ingress = styled.span`
  display: block;
  margin: 0 0 10px 0;
`;

const HeadingPrevious = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  &[role='button'] {
    cursor: pointer;
  }

  h2 {
    margin-right: 10px;
  }
`;

const TableTotal = styled.div`
  display: flex;
  margin-top: 20px;
  padding: 0 4rem;
  & > span {
    margin-left: auto;
    font-weight: 700;
  }
`;

const NewSeagoingButton = styled.div`
  display: flex;
`;

const StyledTooltip = styled(Tooltip)`
  button {
    padding: 0;
  }
`;

const CurrentSeagoing = styled.div`
  margin: 20px 0;

  table > tbody > tr > td:first-child {
    width: 60px;

    span svg {
      font-size: 1.5rem;
    }
  }
`;

const NotRelevantSeagoing = styled.div`
  margin: 20px 0;

  table > tbody > tr > td:first-child {
    width: 60px;

    span svg {
      font-size: 1.5rem;
    }
  }
`;

const PreviouslyApprovedSeagoing = styled.div`
  margin: 40px 0;

  table > tbody > tr > td:first-child {
    width: 60px;

    span svg {
      font-size: 1.5rem;
    }
  }
`;

const MissingDays = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const FilePreview = styled.div`
  height: 100%;
  width: 600px;
  margin: 30px 0 0 30px;

  & > div {
    width: 100%;
    height: auto;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    width: 400px;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    width: 100%;
    margin: 30px 0 0 0;
  }
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
